/**
 * Billboarded point sprites: stars, runway lights, PAPI, and anything else that is a *light*
 * rather than a surface.
 *
 * ## Why this exists rather than `THREE.Points`
 *
 * Because `Points` does not work. From three's own `PointsNodeMaterial` documentation: "WebGPU
 * only supports point primitives with 1 pixel size… If an application wants to render points
 * with a size larger than 1 pixel, the material should be used with Sprite and instancing."
 * So every `material.size = 2.6` in the codebase is silently a 1 px dot on the primary backend
 * and a 2.6 px dot on the fallback — the two backends disagree, and the one that matters is the
 * one that looks worse. A single unlit pixel is not a runway edge light seen from three miles
 * out on final; it is barely visible at all, which is precisely the thing this milestone has to
 * get right for the dusk landing at Innsbruck.
 *
 * So: expand each point into a camera-facing quad in the vertex stage. Four vertices and two
 * triangles per light, one draw call for the whole set, identical on both backends, and full
 * control over the falloff profile.
 *
 * ## The sizing model, which is the interesting part
 *
 * A real light has a physical size *and* a minimum apparent size. Approach a runway and the
 * edge lights grow, because you are getting closer to objects of a fixed size. Keep flying away
 * and they do not shrink to nothing and vanish — they bottom out as points of light and then
 * simply get dimmer, because a light source smaller than the eye's resolution is still a light
 * source. A pure world-space sprite gets the first half right and the second half badly wrong
 * (lights wink out at exactly the distance you most want to see them). A pure screen-space
 * sprite gets it backwards (lights the same size from the threshold as from ten miles).
 *
 * Both behaviours fall out of one line: take the larger of a world size and a pixel floor.
 *
 *   effective radius [m] = max(radiusM, minPixels × distance × metresPerPixelAtOneMetre)
 *
 * Stars set `radiusM = 0`, so they are pure pixel size — correct, since a star has no
 * meaningful physical size here. Runway lights set both, and behave like lights.
 *
 * `updateFrame(alpha)`: yes — {@link SpriteCloud.setViewport} must be told the vertical FOV and
 * viewport height whenever either changes, and {@link SpriteCloud.setOpacity} is how the caller
 * fades a set in and out. Neither allocates.
 */

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Mesh,
  Vector3,
  type Camera,
  type PerspectiveCamera,
} from 'three';
import { MeshBasicNodeMaterial } from 'three/webgpu';
import {
  attribute,
  cameraProjectionMatrix,
  exp,
  float,
  length,
  max,
  modelViewMatrix,
  uniform,
  vec3,
  vec4,
} from 'three/tsl';

/** The four corners of a unit quad, in the order the index buffer below expects. */
const CORNERS = [
  [-1, -1],
  [1, -1],
  [1, 1],
  [-1, 1],
] as const;

export interface SpriteCloudOptions {
  /** Maximum number of sprites. The buffers are allocated once, at this size. */
  capacity: number;
  /**
   * Falloff exponent for the glow profile. Higher is tighter — 4.5 gives a small bright core
   * with a soft halo, which is what an out-of-focus point light looks like.
   */
  falloff?: number;
  /** Name, for the scene graph and for debugging. */
  name?: string;
}

/**
 * A fixed-capacity set of billboarded lights, drawn in one call.
 *
 * Fill it with {@link set}, then {@link commit}. Points past `count` are collapsed to zero size
 * rather than left as stale geometry, so a set can shrink without reallocating.
 */
export class SpriteCloud {
  readonly mesh: Mesh;
  readonly capacity: number;

  private readonly geometry: BufferGeometry;
  private readonly material: MeshBasicNodeMaterial;
  private readonly positions: Float32Array;
  private readonly colors: Float32Array;
  /** Per-sprite (worldRadiusM, minPixels), duplicated across the quad's four vertices. */
  private readonly sizes: Float32Array;

  /** 2·tan(fovY/2)/viewportHeightPx — metres per pixel at one metre of range. */
  private readonly metresPerPixel = uniform(0.001);
  private readonly opacityUniform = uniform(1);
  private countValue = 0;

  constructor(options: SpriteCloudOptions) {
    this.capacity = options.capacity;
    const vertices = this.capacity * 4;

    this.positions = new Float32Array(vertices * 3);
    this.colors = new Float32Array(vertices * 3);
    this.sizes = new Float32Array(vertices * 2);
    const corners = new Float32Array(vertices * 2);
    const indices = new Uint32Array(this.capacity * 6);

    for (let i = 0; i < this.capacity; i++) {
      for (let c = 0; c < 4; c++) {
        corners[(i * 4 + c) * 2] = CORNERS[c][0];
        corners[(i * 4 + c) * 2 + 1] = CORNERS[c][1];
      }
      const base = i * 4;
      indices.set([base, base + 1, base + 2, base, base + 2, base + 3], i * 6);
    }

    this.geometry = new BufferGeometry();
    this.geometry.setAttribute('position', new BufferAttribute(this.positions, 3));
    this.geometry.setAttribute('color', new BufferAttribute(this.colors, 3));
    this.geometry.setAttribute('spriteSize', new BufferAttribute(this.sizes, 2));
    this.geometry.setAttribute('corner', new BufferAttribute(corners, 2));
    this.geometry.setIndex(new BufferAttribute(indices, 1));
    // The cloud is either camera-parented (stars) or covers a whole airport; in both cases a
    // bounding sphere fitted to the rest pose culls it at the worst possible moment.
    this.geometry.boundingSphere = null;
    this.geometry.setDrawRange(0, 0);

    this.material = this.buildMaterial(options.falloff ?? 4.5);

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.name = options.name ?? 'sprite-cloud';
    this.mesh.frustumCulled = false;
    this.mesh.castShadow = false;
    this.mesh.receiveShadow = false;
  }

  private buildMaterial(falloff: number): MeshBasicNodeMaterial {
    const material = new MeshBasicNodeMaterial();
    material.transparent = true;
    material.depthWrite = false;
    // Lights add to what is behind them; they do not replace it. This is also what makes a
    // cluster of approach lights read as brighter than a single one.
    material.blending = AdditiveBlending;

    const centre = attribute<'vec3'>('position', 'vec3');
    const corner = attribute<'vec2'>('corner', 'vec2');
    const size = attribute<'vec2'>('spriteSize', 'vec2');

    const viewPosition = modelViewMatrix.mul(vec4(centre, 1));
    // Range from the eye. Not `−viewPosition.z`: a light 60° off the nose is genuinely further
    // away than its depth suggests, and using depth would make lights at the edge of the screen
    // bloom larger than the ones ahead.
    const range = length(viewPosition.xyz);

    // The sizing model, in one expression. `size.x` is the world radius [m], `size.y` the pixel
    // floor; `metresPerPixel` converts the second into the units of the first at this range.
    const radiusM = max(size.x, size.y.mul(range).mul(this.metresPerPixel));

    const offset = vec3(corner.x.mul(radiusM), corner.y.mul(radiusM), 0);
    material.vertexNode = cameraProjectionMatrix.mul(vec4(viewPosition.xyz.add(offset), 1));

    // A Gaussian-ish core. Not a hard disc: a hard-edged dot reads as a decal, and the soft
    // shoulder is what the eye interprets as "this is emitting light" rather than "this is a
    // grey circle". Costs one exp.
    const radial = length(attribute<'vec2'>('corner', 'vec2'));
    const intensity = exp(radial.mul(radial).mul(-falloff));

    material.colorNode = vec3(attribute<'vec3'>('color', 'vec3')).mul(intensity).mul(this.opacityUniform);
    // Additive blending reads the colour, not the alpha, but three still needs a coherent alpha
    // for its sorting and for the WebGL2 path's blend equation.
    material.opacityNode = float(1);

    return material;
  }

  /** Sprites currently drawn. */
  get count(): number {
    return this.countValue;
  }

  /**
   * Place one sprite.
   *
   * @param index        0…capacity−1.
   * @param position     centre, in the mesh's own space.
   * @param r,g,b        linear colour and brightness; values above 1 are meaningful and bloom.
   * @param worldRadiusM physical radius [m]. 0 for something with no physical size, like a star.
   * @param minPixels    apparent radius the sprite never shrinks below [px].
   */
  set(
    index: number,
    position: Vector3,
    r: number,
    g: number,
    b: number,
    worldRadiusM: number,
    minPixels: number,
  ): void {
    if (index < 0 || index >= this.capacity) return;
    for (let c = 0; c < 4; c++) {
      const v = index * 4 + c;
      this.positions[v * 3] = position.x;
      this.positions[v * 3 + 1] = position.y;
      this.positions[v * 3 + 2] = position.z;
      this.colors[v * 3] = r;
      this.colors[v * 3 + 1] = g;
      this.colors[v * 3 + 2] = b;
      this.sizes[v * 2] = worldRadiusM;
      this.sizes[v * 2 + 1] = minPixels;
    }
  }

  /** Recolour one sprite without touching its position — the PAPI's whole job. */
  setColor(index: number, r: number, g: number, b: number): void {
    if (index < 0 || index >= this.capacity) return;
    for (let c = 0; c < 4; c++) {
      const v = index * 4 + c;
      this.colors[v * 3] = r;
      this.colors[v * 3 + 1] = g;
      this.colors[v * 3 + 2] = b;
    }
    (this.geometry.getAttribute('color') as BufferAttribute).needsUpdate = true;
  }

  /** Publish `count` sprites' worth of the buffers to the GPU. */
  commit(count: number): void {
    this.countValue = Math.min(count, this.capacity);
    (this.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('color') as BufferAttribute).needsUpdate = true;
    (this.geometry.getAttribute('spriteSize') as BufferAttribute).needsUpdate = true;
    this.geometry.setDrawRange(0, this.countValue * 6);
  }

  /** Overall brightness, 0…1. How a set of lights comes up at dusk. */
  setOpacity(opacity: number): void {
    this.opacityUniform.value = Math.max(0, opacity);
  }

  get opacity(): number {
    return this.opacityUniform.value as number;
  }

  /**
   * Tell the cloud how big a pixel is. Must be called on resize and whenever the FOV changes,
   * or every pixel floor in the set is wrong by the ratio of the two viewport heights.
   */
  setViewport(camera: PerspectiveCamera, viewportHeightPx: number): void {
    const halfFov = ((camera.fov * Math.PI) / 180) / 2;
    this.metresPerPixel.value = (2 * Math.tan(halfFov)) / Math.max(1, viewportHeightPx);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}

/**
 * Metres subtended by one pixel at one metre of range — the same quantity {@link SpriteCloud}
 * uses, exposed so callers can size things consistently without duplicating the trigonometry.
 */
export function metresPerPixelAtUnitRange(camera: Camera, viewportHeightPx: number): number {
  const perspective = camera as PerspectiveCamera;
  const halfFov = ((perspective.fov * Math.PI) / 180) / 2;
  return (2 * Math.tan(halfFov)) / Math.max(1, viewportHeightPx);
}
